import React, { useState } from 'react';
import { 
  Key, 
  CreditCard, 
  Globe, 
  Shield, 
  Download,
  Trash2,
  Copy,
  Eye,
  EyeOff,
  RefreshCw,
  AlertTriangle
} from 'lucide-react';

const AccountSettings = () => {
  const [activeTab, setActiveTab] = useState('api');
  const [showApiKey, setShowApiKey] = useState(false);

  // Mock data
  const [settings, setSettings] = useState({
    apiKey: 'climify_sk_1234567890abcdef',
    webhookUrl: 'https://myapp.com/webhooks/climify',
    dataRetention: '12', // months
    privacyLevel: 'standard',
    autoBackup: true,
    billingEmail: 'billing@example.com',
    invoiceDownload: true
  });

  const tabs = [
    { id: 'api', label: 'API Access', icon: Key },
    { id: 'billing', label: 'Billing', icon: CreditCard },
    { id: 'privacy', label: 'Privacy', icon: Shield },
    { id: 'data', label: 'Data Management', icon: Download }
  ];

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    // Show success message
  };

  const regenerateApiKey = () => {
    // Generate new API key
    const newKey = 'climify_sk_' + Math.random().toString(36).substr(2, 16);
    setSettings(prev => ({ ...prev, apiKey: newKey }));
  };

  const renderApiTab = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">API Configuration</h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">API Key</label>
            <div className="flex items-center space-x-2">
              <div className="flex-1 relative">
                <input
                  type={showApiKey ? "text" : "password"}
                  value={settings.apiKey}
                  readOnly
                  className="w-full p-3 border border-gray-300 rounded-lg bg-gray-50 pr-10"
                />
                <button
                  onClick={() => setShowApiKey(!showApiKey)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showApiKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              <button
                onClick={() => copyToClipboard(settings.apiKey)}
                className="p-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <Copy className="h-4 w-4" />
              </button>
              <button
                onClick={regenerateApiKey}
                className="bg-blue-600 text-white px-4 py-3 rounded-lg hover:bg-blue-700 transition-colors flex items-center"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Regenerate
              </button>
            </div>
            <p className="text-sm text-gray-600 mt-1">
              Keep your API key secure. Do not share it publicly.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Webhook URL</label>
            <input
              type="url"
              value={settings.webhookUrl}
              onChange={(e) => setSettings(prev => ({ ...prev, webhookUrl: e.target.value }))}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              placeholder="https://yourapp.com/webhooks/climify"
            />
            <p className="text-sm text-gray-600 mt-1">
              Receive real-time notifications about your projects and transactions.
            </p>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <AlertTriangle className="h-5 w-5 text-yellow-600 mt-0.5" />
              <div>
                <h4 className="font-medium text-yellow-800">Rate Limits</h4>
                <p className="text-sm text-yellow-700 mt-1">
                  Your current plan includes 1,000 API calls per month. 
                  <a href="#" className="underline">Upgrade for higher limits</a>.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="border-t pt-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">API Documentation</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <a
            href="#"
            className="block p-4 border border-gray-200 rounded-lg hover:border-green-300 transition-colors"
          >
            <h4 className="font-medium text-gray-900">Getting Started</h4>
            <p className="text-sm text-gray-600 mt-1">Learn how to integrate with our API</p>
          </a>
          <a
            href="#"
            className="block p-4 border border-gray-200 rounded-lg hover:border-green-300 transition-colors"
          >
            <h4 className="font-medium text-gray-900">API Reference</h4>
            <p className="text-sm text-gray-600 mt-1">Complete API documentation</p>
          </a>
        </div>
      </div>
    </div>
  );

  const renderBillingTab = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Billing Information</h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Billing Email</label>
            <input
              type="email"
              value={settings.billingEmail}
              onChange={(e) => setSettings(prev => ({ ...prev, billingEmail: e.target.value }))}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
            />
          </div>

          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-medium text-gray-900">Enable Invoice Downloads</h4>
              <p className="text-sm text-gray-600">Allow downloading of invoice PDFs</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={settings.invoiceDownload}
                onChange={(e) => setSettings(prev => ({ ...prev, invoiceDownload: e.target.checked }))}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
            </label>
          </div>
        </div>
      </div>

      <div className="border-t pt-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Current Plan</h3>
        <div className="bg-green-50 border border-green-200 rounded-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="text-xl font-semibold text-green-900">Professional Plan</h4>
              <p className="text-green-700">$49/month • Billed monthly</p>
              <ul className="text-sm text-green-700 mt-2 space-y-1">
                <li>• Unlimited projects</li>
                <li>• Advanced verification tools</li>
                <li>• Priority support</li>
                <li>• API access (1,000 calls/month)</li>
              </ul>
            </div>
            <div className="text-right">
              <button className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors mb-2">
                Manage Plan
              </button>
              <p className="text-sm text-green-600">Next billing: Dec 15, 2024</p>
            </div>
          </div>
        </div>
      </div>

      <div className="border-t pt-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Payment Method</h3>
        <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
          <div className="flex items-center space-x-3">
            <CreditCard className="h-8 w-8 text-gray-400" />
            <div>
              <p className="font-medium text-gray-900">•••• •••• •••• 4242</p>
              <p className="text-sm text-gray-600">Expires 12/26</p>
            </div>
          </div>
          <button className="text-blue-600 hover:text-blue-700 text-sm font-medium">
            Update
          </button>
        </div>
      </div>
    </div>
  );

  const renderPrivacyTab = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Privacy Settings</h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Privacy Level</label>
            <select
              value={settings.privacyLevel}
              onChange={(e) => setSettings(prev => ({ ...prev, privacyLevel: e.target.value }))}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
            >
              <option value="minimal">Minimal - Essential data only</option>
              <option value="standard">Standard - Balanced privacy and functionality</option>
              <option value="enhanced">Enhanced - Full feature set</option>
            </select>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="font-medium text-blue-900 mb-2">Data Collection</h4>
            <div className="space-y-2 text-sm text-blue-800">
              <p>• Profile information (name, email, company)</p>
              <p>• Project data and verification records</p>
              <p>• Usage analytics for platform improvement</p>
              <p>• Communication preferences</p>
            </div>
          </div>
        </div>
      </div>

      <div className="border-t pt-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Data Rights</h3>
        <div className="space-y-3">
          <button className="w-full text-left p-4 border border-gray-200 rounded-lg hover:border-gray-300 transition-colors">
            <h4 className="font-medium text-gray-900">Download My Data</h4>
            <p className="text-sm text-gray-600 mt-1">Export all your personal data in a portable format</p>
          </button>
          
          <button className="w-full text-left p-4 border border-gray-200 rounded-lg hover:border-gray-300 transition-colors">
            <h4 className="font-medium text-gray-900">Data Portability</h4>
            <p className="text-sm text-gray-600 mt-1">Transfer your data to another service</p>
          </button>
          
          <button className="w-full text-left p-4 border border-red-200 rounded-lg hover:border-red-300 transition-colors">
            <h4 className="font-medium text-red-900">Request Data Deletion</h4>
            <p className="text-sm text-red-600 mt-1">Permanently delete your account and all associated data</p>
          </button>
        </div>
      </div>
    </div>
  );

  const renderDataTab = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Data Management</h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Data Retention Period</label>
            <select
              value={settings.dataRetention}
              onChange={(e) => setSettings(prev => ({ ...prev, dataRetention: e.target.value }))}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
            >
              <option value="6">6 months</option>
              <option value="12">12 months</option>
              <option value="24">24 months</option>
              <option value="indefinite">Indefinite</option>
            </select>
            <p className="text-sm text-gray-600 mt-1">
              How long to keep your project data and transaction history
            </p>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-medium text-gray-900">Automatic Backup</h4>
              <p className="text-sm text-gray-600">Automatically backup your data weekly</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={settings.autoBackup}
                onChange={(e) => setSettings(prev => ({ ...prev, autoBackup: e.target.checked }))}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
            </label>
          </div>
        </div>
      </div>

      <div className="border-t pt-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Export Data</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <button className="p-4 border border-gray-200 rounded-lg hover:border-green-300 transition-colors text-left">
            <Download className="h-6 w-6 text-green-600 mb-2" />
            <h4 className="font-medium text-gray-900">Project Data</h4>
            <p className="text-sm text-gray-600 mt-1">Export all your carbon credit projects</p>
          </button>
          
          <button className="p-4 border border-gray-200 rounded-lg hover:border-green-300 transition-colors text-left">
            <Download className="h-6 w-6 text-green-600 mb-2" />
            <h4 className="font-medium text-gray-900">Transaction History</h4>
            <p className="text-sm text-gray-600 mt-1">Download complete transaction records</p>
          </button>
          
          <button className="p-4 border border-gray-200 rounded-lg hover:border-green-300 transition-colors text-left">
            <Download className="h-6 w-6 text-green-600 mb-2" />
            <h4 className="font-medium text-gray-900">Verification Reports</h4>
            <p className="text-sm text-gray-600 mt-1">Export all verification documentation</p>
          </button>
          
          <button className="p-4 border border-gray-200 rounded-lg hover:border-green-300 transition-colors text-left">
            <Download className="h-6 w-6 text-green-600 mb-2" />
            <h4 className="font-medium text-gray-900">Complete Archive</h4>
            <p className="text-sm text-gray-600 mt-1">Full account data export</p>
          </button>
        </div>
      </div>

      <div className="border-t pt-6">
        <h3 className="text-lg font-semibold text-red-600 mb-4">Danger Zone</h3>
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-medium text-red-900">Clear All Data</h4>
              <p className="text-sm text-red-600">Permanently delete all your account data</p>
            </div>
            <button className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors flex items-center">
              <Trash2 className="h-4 w-4 mr-2" />
              Clear Data
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case 'api':
        return renderApiTab();
      case 'billing':
        return renderBillingTab();
      case 'privacy':
        return renderPrivacyTab();
      case 'data':
        return renderDataTab();
      default:
        return renderApiTab();
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Account Settings</h1>
          <p className="text-gray-600 mt-1">Manage your account configuration and data</p>
        </div>

        {/* Tab Navigation */}
        <div className="border-b border-gray-200 mb-6">
          <nav className="-mb-px flex space-x-8">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab.id
                    ? 'border-green-500 text-green-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <tab.icon className="h-5 w-5 mr-2" />
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="bg-white rounded-lg shadow p-6">
          {renderTabContent()}
        </div>
      </div>
    </div>
  );
};

export default AccountSettings; 